/**
 * CivicPulse — Authority directory + inbox drill-down.
 *
 * URL scheme:
 *   /civic/authorities           → directory (list all authorities)
 *   /civic/authorities/:id       → that authority's inbox
 * (or root-relative on the civicpulse.blog apex)
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, ArrowRight, Loader2, MapPin, CheckCircle2, Inbox,
  ChevronLeft, ExternalLink,
} from "lucide-react";
import { civic, photoUrl } from "./api";
import { civicPath } from "./basePath";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/civicpulse/v1`;

async function fetchAuthorities(geoPath) {
  const qs = geoPath ? `?geo_path=${encodeURIComponent(geoPath)}` : "";
  const res = await fetch(`${API}/authorities${qs}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function fetchInbox(id, status = "") {
  const qs = status ? `?status=${status}` : "";
  const res = await fetch(`${API}/authorities/${encodeURIComponent(id)}/inbox${qs}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function CivicAuthorityDirectory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchAuthorities().then((r) => setItems(r.items || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-authorities-directory">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-semibold uppercase tracking-[.22em] text-emerald-700 mb-2">
          For Officials & Authorities
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">
          <Building2 className="inline w-6 h-6 -mt-1 mr-2 text-emerald-700" /> Authority Directory
        </h1>
        <p className="text-slate-600 mt-3 text-sm max-w-2xl">
          These verified authorities receive assigned issues, respond publicly, and upload before-and-after evidence. Click any authority to see their live inbox.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {loading ? (
            <div className="col-span-full py-10 grid place-items-center"><Loader2 className="w-6 h-6 text-emerald-700 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="col-span-full rounded-2xl bg-white ring-1 ring-emerald-100 p-8 text-center text-slate-500 italic">
              No authorities configured yet.
            </div>
          ) : (
            items.map((a) => (
              <Link
                key={a.id}
                to={civicPath(`/authorities/${a.id}`)}
                data-testid={`civic-authority-${a.id}`}
                className="rounded-2xl bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow transition p-5"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                    <Building2 className="w-6 h-6" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                      <MapPin className="w-3 h-3" /> {a.jurisdiction?.geo_path_prefix}
                      {a.verified && (
                        <span className="ml-1 inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Handles: {a.categories?.slice(0, 4).join(", ")}{a.categories?.length > 4 ? "…" : ""}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-emerald-600 shrink-0 mt-1" />
                </div>
              </Link>
            ))
          )}
        </div>
        <div className="mt-10 rounded-2xl bg-white ring-1 ring-emerald-100 p-5 text-sm">
          <div className="font-semibold text-slate-800 mb-2">Want to onboard your authority?</div>
          <p className="text-slate-600">
            Email <a className="underline text-emerald-700" href="mailto:hello@civicpulse.blog">hello@civicpulse.blog</a> with
            your jurisdiction, categories handled, and a verification contact. We'll enable your inbox within 1 working day.
          </p>
        </div>
      </div>
    </div>
  );
}

export function CivicAuthorityInbox() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    setLoading(true);
    fetchInbox(id, filter).then(setData).finally(() => setLoading(false));
  }, [id, filter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] grid place-items-center">
        <Loader2 className="w-6 h-6 text-emerald-700 animate-spin" />
      </div>
    );
  }
  if (!data?.authority) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-2xl text-slate-800 mb-2">Authority not found</h1>
          <Link to={civicPath("/authorities")} className="text-emerald-700 underline">Back to directory</Link>
        </div>
      </div>
    );
  }
  const a = data.authority;
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-authority-inbox">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to={civicPath("/authorities")}
              className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 mb-3">
          <ChevronLeft className="w-3.5 h-3.5" /> All authorities
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div className="flex items-start gap-4">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 shrink-0">
              <Building2 className="w-7 h-7" />
            </span>
            <div>
              <h1 className="font-serif text-3xl font-bold text-slate-900">{a.name}</h1>
              <div className="text-sm text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                <MapPin className="w-3.5 h-3.5" /> {a.jurisdiction?.geo_path_prefix}
                {a.verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                    <CheckCircle2 className="w-3 h-3" /> Verified
                  </span>
                )}
                {a.contact?.website && (
                  <a href={a.contact.website} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
                    Official site <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 text-sm">
            <div className="rounded-xl bg-white ring-1 ring-emerald-100 px-4 py-2">
              <div className="text-xs text-slate-500">Open</div>
              <div className="text-xl font-serif font-bold text-slate-900 tabular-nums">{data.open}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-2">
              <div className="text-xs text-emerald-700">Resolved</div>
              <div className="text-xl font-serif font-bold text-emerald-800 tabular-nums">{data.resolved}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white ring-1 ring-emerald-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-emerald-100 flex items-center justify-between">
            <div className="font-semibold text-slate-800 inline-flex items-center gap-2">
              <Inbox className="w-4 h-4 text-emerald-700" /> Assigned Issues
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
                    data-testid="civic-inbox-filter-status"
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              <option value="">All</option>
              <option value="routed">Routed</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="work_underway">Work underway</option>
              <option value="claimed_resolved">Claimed resolved</option>
              <option value="community_verified">Community verified</option>
            </select>
          </div>
          {data.items.length === 0 ? (
            <div className="p-8 text-center text-slate-500 italic text-sm">
              No issues assigned {filter ? "with this status" : "yet"}.
            </div>
          ) : (
            <ul className="divide-y divide-emerald-50">
              {data.items.map((it) => (
                <li key={it.public_id} className="p-4 hover:bg-emerald-50/40 transition">
                  <Link to={civicPath(`/issue/${it.public_id}`)}
                        className="flex items-start gap-3">
                    {it.photo_urls?.[0] && (
                      <img src={photoUrl(it.photo_urls[0])} alt=""
                           className="w-16 h-16 object-cover rounded-lg ring-1 ring-slate-200 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
                        {it.category}
                      </div>
                      <div className="font-semibold text-slate-800 truncate">{it.title}</div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{it.public_id}</span>
                        <span>•</span>
                        <span>{it.location?.address || it.location?.city || ""}</span>
                        <span>•</span>
                        <span className="capitalize">{it.status?.replace(/_/g, " ")}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-emerald-600 self-center shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
